import { useState } from "react";
import {
  Row,
  Col,
  Card,
  CardTitle,
  CardSubtitle,
  Form,
  Label,
  Input,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import PacmanLoader from "../Pacmanloader/Pacmanloader";
import {
  HandleUserNameChange,
  HandleUserEmailChange,
  CheckIfEmailIsOccupied,
  UpdateTeacherPermissions,
} from "../../actions/teacherMutations";
import { toast } from "react-toastify";
import { checkNameLengthAndCharacters } from "../../Utilities/checkerFunctions";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import ConfirmDialog from "../Modal/ConfirmDialog";
import TeacherPermissionFields from "./TeacherPermissionFields";

const PermissionPrompts = {
  fullAccess: {
    give: "SureYouWantToGiveFullAccess",
    remove: "SureYouWantToRemoveFullAccess",
    removeOwn: "SureYouWantToRemoveOwnFullAccess",
  },
  purchaser: {
    give: "SureYouWantToGivePurchaser",
    remove: "SureYouWantToRemovePurchaser",
    removeOwn: "SureYouWantToRemoveOwnPurchaser",
  },
};

export default function TeacherAdmin(props) {
  const intl = useIntl();
  const [updatingTeacher, setUpdatingTeacher] = useState(false);
  const [emailOccupied, setEmailOccupied] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [pendingPermission, setPendingPermission] = useState(null);

  const queryClient = useQueryClient();

  const mutateTeacherName = useMutation({ mutationFn: HandleUserNameChange });
  const mutateTeacherEmail = useMutation({ mutationFn: HandleUserEmailChange });
  const mutateCheckTeacherEmail = useMutation({ mutationFn: CheckIfEmailIsOccupied });
  const mutateTeacherPermissions = useMutation({
    mutationFn: UpdateTeacherPermissions,
  });

  if (props.teacherData.isPending || props.teacherInfo.isPending) {
    return <PacmanLoader color={"#20a8d8"} size={15} loading={true} />;
  }

  let values = {
    oldEmail: props.teacherData.data.email,
    newEmail: props.teacherData.data.email,
    oldName: props.teacherData.data.name,
    newName: props.teacherData.data.name,
  };

  const savePendingPermission = () => {
    mutateTeacherPermissions.mutate(
      {
        teacherId: props.teacherData.data.id,
        fullAccess: props.teacherData.data.fullAccess,
        purchaser: props.teacherData.data.purchaser,
        [pendingPermission.field]: pendingPermission.value,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["FetchTeacherInfoById"] });
          if (props.teacherData.data.id === props.teacherInfo.data.id) {
            queryClient.invalidateQueries({
              queryKey: ["FetchTeacherInformation"],
            });
          }
          toast.success(<FormattedMessage id="PermissionsUpdated" />);
        },
        onError: (error) => {
          toast.warn(formatServiceError(intl, error?.response?.data));
        },
        onSettled: () => setPendingPermission(null),
      },
    );
  };

  const handleChangedTeacherName = (event) => {
    if (event.target.value !== props.teacherData.data.name) {
      const nameCheck = checkNameLengthAndCharacters(event.target.value);
      if (nameCheck !== true) {
        toast.warn(nameCheck);
      } else {
        setUpdatingTeacher(true);
        mutateTeacherName.mutate(
          {
            oldEmail: values.oldEmail,
            newEmail: values.oldEmail,
            newName:
              event.target.name === "name" &&
              event.target.value !== props.teacherData.data.name
                ? event.target.value
                : values.newName,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
              setUpdatingTeacher(false);
              toast.success(intl.formatMessage({ id: "TeacherUpdated" }));
            },
            onError: (err) => {
              setUpdatingTeacher(false);
              toast.warn(formatServiceError(intl, err?.response?.data));
            },
          }
        );
      }
    }
  };

  const checkEmailOnChange = (event) => {
    if (event.target.value !== props.teacherData.data.email) {
      if (
        !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
          event.target.value
        )
      ) {
        setInvalidEmail(true);
      } else {
        setInvalidEmail(false);
        mutateCheckTeacherEmail.mutate(
          {
            oldEmail: values.oldEmail,
            newEmail:
              event.target.name === "email" &&
              event.target.value !== props.teacherData.data.email
                ? event.target.value
                : values.newEmail,
            userName:
              event.target.name === "name" &&
              event.target.value !== props.teacherData.data.name
                ? event.target.value
                : values.newName,
          },
          {
            onSuccess: (data) => {
              if (data.data === true) {
                setEmailOccupied(true);
              } else {
                setEmailOccupied(false);
                handleChangedTeacherEmail(event);
              }
            },
            onError: (err) => {
              setUpdatingTeacher(false);
              toast.warn(formatServiceError(intl, err?.response?.data));
            },
          }
        );
      }
    }
  };

  const handleChangedTeacherEmail = (event) => {
    if (event.target.value !== props.teacherData.data.email) {
      setUpdatingTeacher(true);
      mutateTeacherEmail.mutate(
        {
          oldEmail: values.oldEmail,
          newEmail:
            event.target.name === "email" &&
            event.target.value !== props.teacherData.data.email
              ? event.target.value
              : values.newEmail,
          userName:
            event.target.name === "name" &&
            event.target.value !== props.teacherData.data.name
              ? event.target.value
              : values.newName,
        },
        {
          onSuccess: () => {
            setUpdatingTeacher(false);
            toast.success(<FormattedMessage id="TeacherUpdated" />);
            queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
          },
          onError: (err) => {
            setUpdatingTeacher(false);
            toast.warn(formatServiceError(intl, err?.response?.data));
          },
        }
      );
    }
  };

  return (
    <div className="animated fadeIn">
      <Col lg={12}>
        <Card body>
          <CardTitle>
            <FormattedMessage id="TeacherInformation" />
            {updatingTeacher ? (
              <div style={{ float: "right" }}>
                {" "}
                <div style={{ marginTop: "-11%", marginBottom: "22%" }}>
                  <PacmanLoader color={"#20a8d8"} size={15} loading={true} />
                </div>
                <FormattedMessage id="Updating" />
              </div>
            ) : (
              ""
            )}
            <hr />
          </CardTitle>
          <CardSubtitle>
            <Row>
              <Col sm="6">
                <Form action="" method="post">
                  {props.teacherInfo.data.fullAccess ? (
                    <>
                      <Label htmlFor="nf-name" className="form-label">
                        <FormattedMessage id="Name" />:
                      </Label>
                      <Input
                        type="text"
                        id="name"
                        name="name"
                        onBlur={handleChangedTeacherName}
                        key={props.teacherData.data.email}
                        defaultValue={props.teacherData.data.name}
                        autoComplete="false"
                      />
                    </>
                  ) : (
                    <>
                      <Row>
                        <Col>
                          <FormattedMessage id="Name" />:
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col>{props.teacherData.data.name}</Col>
                      </Row>
                    </>
                  )}
                  {props.teacherInfo.data.fullAccess ? (
                    <>
                      <Label htmlFor="email" className="form-label">
                        <FormattedMessage id="Email" />:
                      </Label>
                      {emailOccupied ? (
                        <span className="ml-2 text-danger">
                          <FontAwesomeIcon
                            icon="exclamation"
                            className="mr-1"
                          />
                          <FormattedMessage id="EmailOccupied " />
                        </span>
                      ) : (
                        ""
                      )}
                      {invalidEmail ? (
                        <span className="ml-2 text-danger">
                          <FontAwesomeIcon
                            icon="exclamation"
                            className="mr-1"
                          />
                          <FormattedMessage id="WrongEmailFormat " />
                        </span>
                      ) : (
                        ""
                      )}
                      <Input
                        type="email"
                        id="email"
                        name="email"
                        onBlur={checkEmailOnChange}
                        key={props.teacherData.data.email}
                        defaultValue={props.teacherData.data.email}
                        autoComplete="false"
                      />
                    </>
                  ) : (
                    <>
                      <Row>
                        <Col>
                          <FormattedMessage id="Email" />:
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col>{props.teacherData.data.email}</Col>
                      </Row>
                    </>
                  )}
                  {props.teacherInfo.data.fullAccess ? (
                    <TeacherPermissionFields
                      values={props.teacherData.data}
                      grantor={props.teacherInfo.data}
                      onToggle={(field, value) =>
                        setPendingPermission({ field, value })
                      }
                    />
                  ) : (
                    ""
                  )}
                </Form>
              </Col>
            </Row>
          </CardSubtitle>
        </Card>
      </Col>
      <ConfirmDialog
        isOpen={pendingPermission !== null}
        toggle={() => setPendingPermission(null)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          pendingPermission ? (
            <FormattedMessage
              id={
                PermissionPrompts[pendingPermission.field][
                  pendingPermission.value
                    ? "give"
                    : props.teacherData.data.id === props.teacherInfo.data.id
                      ? "removeOwn"
                      : "remove"
                ]
              }
              values={{ name: props.teacherData.data.name }}
            />
          ) : (
            ""
          )
        }
        onProceed={savePendingPermission}
        danger={pendingPermission !== null && !pendingPermission.value}
      />
    </div>
  );
}
